import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  useNavigation,
  useRoute,
  RouteProp,
  CompositeNavigationProp,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Snackbar from '~/components/Snackbar';
import { useAuth } from '~/context/auth';
import useAuthTeams from '~/data/hooks/useAuthTeams';
import usePollVote from '~/data/hooks/usePollVote';
import usePosts from '~/data/hooks/usePosts';
import { BestMatchedAsset } from '~/data/models/campaign';
import { Post } from '~/data/models/post';
import { TeamDetail, TeamUser } from '~/data/models/team';
import {
  ACCEPT_USER_IN_TEAM,
  ACTIVATE_TEAM,
  ADD_USER_TO_TEAM,
  REMOVE_USER_FROM_TEAM,
  TEAM_DETAIL,
  USER_TEAM,
} from '~/data/operations/team';
import {
  readTeamDetailFragment,
  readTeamSampleFragment,
  writeTeamDetailFragment,
  writeTeamSampleFragment,
} from '~/data/operations/team/helpers';
import { Status, TeamType, VariantTypeEnum } from '~/data/types/graphql';
import { PlanningStackParamList, RootStackParamList } from '~/navigation/types';
import useAdsTracking from '~/utils/hooks/useAdsTracking';
import { t } from '~/utils/i18n';
import TeamDetailLayout from './layout';

type TeamDetailNavProp = CompositeNavigationProp<
  StackNavigationProp<PlanningStackParamList, 'TeamDetail'>,
  StackNavigationProp<RootStackParamList>
>;

type TeamDetailRouteProp = RouteProp<PlanningStackParamList, 'TeamDetail'>;

export default function TeamDetailScreen(): JSX.Element {
  const navigation = useNavigation<TeamDetailNavProp>();
  const {
    params: { teamId },
  } = useRoute<TeamDetailRouteProp>();

  const { authUserId, refetchAuthUserGroup } = useAuth();

  const { authTeamIds } = useAuthTeams();

  const {
    data: teamData,
    loading: teamLoading,
    refetch: refetchTeam,
  } = useQuery(TEAM_DETAIL, {
    skip: !authUserId,
    variables: {
      id: teamId,
    },
  });
  const team = teamData?.team as TeamDetail;
  const teamUsers: TeamUser[] =
    (team?.users?.map((user) => user) as TeamUser[]) || [];

  const acceptedTeamUsers = teamUsers.filter(
    (teamUser) => teamUser.status === Status.ACCEPTED,
  );

  const pendingTeamUsers = teamUsers.filter(
    (teamUser) => teamUser.status === Status.PENDING,
  );

  const adminUserIds = teamUsers
    .filter((teamUser) => teamUser.isAdmin)
    .map((teamUser) => teamUser.user.id);

  const loggedTeamUser = teamUsers.find(
    (teamUser) => teamUser.user.id === authUserId,
  );

  const checkAuthUserRefetch = (userTeam: TeamUser) => {
    if (
      userTeam?.status === Status.ACCEPTED &&
      (team.type === TeamType.YEARBOOK || team.type === TeamType.CORE)
    ) {
      refetchAuthUserGroup();
    }
  };

  useQuery(USER_TEAM, {
    skip:
      !loggedTeamUser ||
      loggedTeamUser.status !== Status.PENDING ||
      team.isHidden,
    variables: {
      id: loggedTeamUser?.id as string,
    },
    pollInterval: 5000,
    onCompleted: ({ userTeam }) => {
      userTeam && checkAuthUserRefetch(userTeam);
    },
  });

  const {
    posts,
    loading: postsLoading,
    hasNextPage: postsHasNextPage,
    onRefresh: onRefreshPosts,
    onLoadMore: onLoadMorePosts,
    onLikePost,
    onDeletePost,
    onReportPost,
    onReportUser,
    onBlockUser,
    onHidePost,
  } = usePosts({ teamId, skip: !team || team.isHidden || !authUserId });
  const { onPollVote } = usePollVote();
  const [removeUserFromTeam] = useMutation(REMOVE_USER_FROM_TEAM, {});
  const [acceptUserInTeam] = useMutation(ACCEPT_USER_IN_TEAM);
  const [activateTeam, { loading: activateTeamLoading }] =
    useMutation(ACTIVATE_TEAM);

  const {
    bestMatchedAsset,
    refetch: refetchAds,
    randomIndex,
  } = useAdsTracking({
    authUserId,
    variantType: VariantTypeEnum.SMALL,
  });

  let combinedData: (BestMatchedAsset | Post)[] = [...posts];

  if (bestMatchedAsset?.[randomIndex] !== undefined) {
    combinedData = [
      bestMatchedAsset?.[randomIndex] as BestMatchedAsset,
      ...posts,
    ];
  }

  const [addUserToTeam] = useMutation(ADD_USER_TO_TEAM, {
    update(cache, { data }) {
      const newTeamUser = data?.addUserToTeam?.userTeam;

      const currentTeam = readTeamDetailFragment({
        cache,
        id: teamId,
      });

      const currentTeamSample = readTeamSampleFragment({
        cache,
        id: teamId,
      });

      if (currentTeam && newTeamUser) {
        const currentTeamUsers = currentTeam?.users || [];
        const updatedTeamUsers = [...currentTeamUsers, newTeamUser];

        writeTeamDetailFragment({
          cache,
          id: teamId,
          data: {
            ...currentTeam,
            users: updatedTeamUsers,
          },
        });

        if (currentTeamSample && newTeamUser.status === Status.ACCEPTED) {
          writeTeamSampleFragment({
            cache,
            id: teamId,
            data: {
              ...currentTeamSample,
              usersCount: updatedTeamUsers?.length || 0,
              sampleMembers: updatedTeamUsers,
            },
          });
        }
      }
    },
  });

  async function onRequestAccess() {
    try {
      const { data } = await addUserToTeam({
        variables: {
          input: {
            userId: authUserId,
            teamId,
          },
        },
      });

      const userTeam = data?.addUserToTeam?.userTeam;

      if (userTeam?.status === Status.PENDING) {
        Snackbar.show(t('teamDetail.requestSent'), {
          duration: Snackbar.durations.LONG,
          position: Snackbar.positions.BOTTOM_TAB,
          actionText: t('undo'),
          onActionPress: async () => {
            onRemoveRequest();
          },
        });
      } else {
        userTeam && checkAuthUserRefetch(userTeam);
      }
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message, {
          duration: Snackbar.durations.LONG,
          position: Snackbar.positions.BOTTOM_TAB,
        });
      }
    }
  }

  async function onRemoveRequest() {
    try {
      await removeUserFromTeam({
        variables: {
          input: {
            userId: authUserId,
            teamId,
          },
        },
        update(cache) {
          const currentTeam = readTeamDetailFragment({
            cache,
            id: teamId,
          });

          const currentTeamSample = readTeamSampleFragment({
            cache,
            id: teamId,
          });

          if (currentTeam) {
            const currentTeamUsers = currentTeam?.users || [];
            const updatedTeamUsers = currentTeamUsers.filter(
              (teamUser) => teamUser && teamUser.user.id != authUserId,
            );
            writeTeamDetailFragment({
              cache,
              id: teamId,
              data: {
                ...currentTeam,
                users: updatedTeamUsers,
              },
            });

            if (currentTeamSample) {
              writeTeamSampleFragment({
                cache,
                id: teamId,
                data: {
                  ...currentTeamSample,
                  usersCount: updatedTeamUsers?.length || 0,
                  sampleMembers: updatedTeamUsers,
                },
              });
            }
          }
        },
      });
      Snackbar.show(t('teamDetail.requestRemoved'), {
        duration: Snackbar.durations.LONG,
        position: Snackbar.positions.BOTTOM_TAB,
      });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message, {
          duration: Snackbar.durations.LONG,
          position: Snackbar.positions.BOTTOM_TAB,
        });
      }
    }
  }

  const onAcceptUserInTeam = async (userId: string, accept: boolean) => {
    try {
      if (teamId) {
        await acceptUserInTeam({
          variables: {
            input: {
              teamId,
              userId,
              accept,
            },
          },
          update(cache, { data }) {
            const acceptedTeamUser = data?.acceptUserInTeam?.userTeam;

            const currentTeam = readTeamDetailFragment({
              cache,
              id: teamId,
            });

            const currentTeamSample = readTeamSampleFragment({
              cache,
              id: teamId,
            });

            if (currentTeamSample && acceptedTeamUser) {
              writeTeamSampleFragment({
                cache,
                id: teamId,
                data: {
                  ...currentTeamSample,
                  usersCount: currentTeam?.users?.length || 0,
                  sampleMembers: currentTeam?.users || [],
                },
              });
            }
          },
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  const onActivateTeam = async () => {
    try {
      await activateTeam({
        variables: {
          input: {
            id: teamId,
          },
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  const openBannersInBrowser = (url: string) => {
    navigation.navigate('Browser', {
      title: 'Banner',
      url,
    });
  };

  return (
    <TeamDetailLayout
      authUserId={authUserId}
      loggedTeamUser={loggedTeamUser}
      team={team}
      loading={teamLoading || postsLoading}
      posts={combinedData}
      teamUsers={acceptedTeamUsers}
      pendingTeamUsers={pendingTeamUsers}
      adminUserIds={adminUserIds}
      postsHasNextPage={postsHasNextPage}
      authTeamIds={authTeamIds}
      activateTeamLoading={activateTeamLoading}
      openBannersInBrowser={openBannersInBrowser}
      onBack={() => navigation.goBack()}
      onSetting={() => navigation.navigate('EditTeam', { teamId })}
      onChat={() => console.log('onChat')} //eslint-disable-line
      onNotification={() => console.log('onNotification')} //eslint-disable-line
      onCreatePost={() => navigation.navigate('CreatePost', { teamId })}
      onCreatePoll={() => navigation.navigate('CreatePoll', { teamId })}
      onRequestAccess={() => onRequestAccess()}
      onLike={onLikePost}
      onDelete={onDeletePost}
      onReportPost={onReportPost}
      onReportUser={onReportUser}
      onBlockUser={onBlockUser}
      onHidePost={onHidePost}
      onPollVote={onPollVote}
      onComment={(postId) => navigation.navigate('Comments', { postId })}
      onLoadMorePosts={onLoadMorePosts}
      onRefresh={() => {
        onRefreshPosts();
        refetchAds();
        refetchTeam();
      }}
      onDeclineUser={(userId) => onAcceptUserInTeam(userId, false)}
      onAcceptUser={(userId) => onAcceptUserInTeam(userId, true)}
      onActivateTeam={onActivateTeam}
      onEdit={(postId) => navigation.navigate('EditPoll', { postId })}
    />
  );
}
